import { useCallback, useEffect, useRef, useState } from 'react';

export interface DragResizeLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface UseDragResizeOptions {
  parentRef: React.RefObject<HTMLElement>;
  initial: DragResizeLayout;
  /** Hard floor for width/height (pixels). Defaults to 200×160. */
  minSize?: { width: number; height: number };
  /** Top inset (pixels) the widget can't overlap — e.g. the tools bar. */
  topInset?: number;
  /** Called whenever layout changes (drag/resize tick). Use to persist. */
  onChange?: (layout: DragResizeLayout) => void;
}

export interface UseDragResizeResult {
  layout: DragResizeLayout;
  /** Spread onto the drag handle (header) — initiates a drag. */
  dragHandleProps: { onMouseDown: (e: React.MouseEvent) => void };
  /** Spread onto the resize handle (corner) — initiates a resize. */
  resizeHandleProps: { onMouseDown: (e: React.MouseEvent) => void };
  /** Imperatively replace the layout (e.g. when the parent shrinks below the widget). */
  setLayout: (layout: DragResizeLayout) => void;
}

/**
 * Drag/resize hook clamped to a parent element's bounding rect.
 *
 * Drag the dragHandle to translate the widget in (x, y) (pixels relative to
 * the parent). Drag the resizeHandle to grow/shrink (width, height).
 * Both stay within the parent — the widget's right/bottom edge can't escape
 * the parent's edge.
 *
 * Listens to mousemove on `window` during a gesture so the user can drag
 * past the widget edge without losing the gesture.
 */
export function useDragResize(opts: UseDragResizeOptions): UseDragResizeResult {
  const minW = opts.minSize?.width ?? 200;
  const minH = opts.minSize?.height ?? 160;
  const topInset = opts.topInset ?? 0;

  const [layout, setLayoutState] = useState<DragResizeLayout>(opts.initial);
  const layoutRef = useRef(layout);
  const onChangeRef = useRef(opts.onChange);

  // Mirror the latest `layout` and `onChange` into refs from an effect.
  // Drag/resize handlers (set up below) read these refs at gesture time
  // without re-binding when state changes — and the lint rule that bans
  // ref writes during render is satisfied.
  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);
  useEffect(() => {
    onChangeRef.current = opts.onChange;
  }, [opts.onChange]);

  // Sync external `initial` updates into local state. Used when cloud-synced
  // properties resolve asynchronously (page reload: defaults render first,
  // real saved values arrive a tick later).
  //
  // Pattern from React docs ("Storing information from previous renders"):
  // remember the last `initial` we saw and detect prop changes synchronously
  // during render. Calling setState during render is OK when guarded — React
  // discards the in-progress render and re-runs with the new state. This
  // avoids the cascading-render footgun that effect-based syncs hit.
  //
  // Drag tick writes back via `onChange` → cloud-property → arrives back as
  // a new `initial` object whose primitives equal local state → equality
  // check short-circuits → no feedback loop.
  const initX = opts.initial.x;
  const initY = opts.initial.y;
  const initW = opts.initial.width;
  const initH = opts.initial.height;
  const [lastSyncedInit, setLastSyncedInit] = useState({ x: initX, y: initY, width: initW, height: initH });
  if (lastSyncedInit.x !== initX || lastSyncedInit.y !== initY || lastSyncedInit.width !== initW || lastSyncedInit.height !== initH) {
    setLastSyncedInit({ x: initX, y: initY, width: initW, height: initH });
    setLayoutState({ x: initX, y: initY, width: initW, height: initH });
  }

  const setLayout = useCallback((next: DragResizeLayout) => {
    setLayoutState(next);
    onChangeRef.current?.(next);
  }, []);

  // Re-clamp on parent resize so the widget can never end up outside the
  // viewport (e.g. user resizes the browser to be smaller than the widget).
  useEffect(() => {
    const parent = opts.parentRef.current;
    if (!parent) return;

    const observer = new ResizeObserver(() => {
      const rect = parent.getBoundingClientRect();
      const clamped = clampLayout(layoutRef.current, rect, minW, minH, topInset);
      if (clamped !== layoutRef.current) {
        setLayout(clamped);
      }
    });
    observer.observe(parent);
    return () => observer.disconnect();
  }, [opts.parentRef, minW, minH, topInset, setLayout]);

  // ---- Drag (translate) -----------------------------------------------
  //
  // Plain functions, not useCallback. Each handler runs once per gesture
  // start; allocating a fresh function per render is negligible and avoids
  // the React Compiler lint rule that forbids `useCallback` deps from
  // including `.current` access on a ref. (The handler reads
  // `opts.parentRef.current` at call time, which the rule can't track.)

  const onDragMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const start = { ...layoutRef.current };
    const parent = opts.parentRef.current;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const next = clampLayout({ ...start, x: start.x + dx, y: start.y + dy }, parentRect, minW, minH, topInset);
      setLayout(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ---- Resize (bottom-right corner) -----------------------------------

  const onResizeMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const start = { ...layoutRef.current };
    const parent = opts.parentRef.current;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const next = clampLayout({ ...start, width: start.width + dx, height: start.height + dy }, parentRect, minW, minH, topInset);
      setLayout(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return {
    layout,
    dragHandleProps: { onMouseDown: onDragMouseDown },
    resizeHandleProps: { onMouseDown: onResizeMouseDown },
    setLayout,
  };
}

/**
 * Clamp a layout to fit inside a parent rectangle.
 *
 * `topInset` reserves vertical space at the top of the parent (e.g. the tools
 * bar) — the widget's top edge can't go above it. Returns the input unchanged
 * when already in bounds (referential equality, lets callers skip setState).
 */
function clampLayout(layout: DragResizeLayout, parentRect: DOMRect, minW: number, minH: number, topInset: number): DragResizeLayout {
  const availH = Math.max(minH, parentRect.height - topInset);
  const maxW = Math.max(minW, parentRect.width);
  const width = Math.max(minW, Math.min(layout.width, maxW));
  const height = Math.max(minH, Math.min(layout.height, availH));
  const x = Math.max(0, Math.min(layout.x, parentRect.width - width));
  const y = Math.max(topInset, Math.min(layout.y, parentRect.height - height));
  if (width === layout.width && height === layout.height && x === layout.x && y === layout.y) {
    return layout;
  }
  return { x, y, width, height };
}
